import { useUpdate } from 'ahooks'
import { Dropdown, List, Menu } from 'antd'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrivalOrder, ChevronDown, ChevronRight, ChevronUp, Country, Edit, Language as LanguageIcon, LogOut, Setting, User } from '@yy/one-icon'
import { OneButton, OneTooltip } from '@yy/one-ui'
import Avatar from '@/bizComponent/SvgIcons/Avatar'
import { useUserLogin } from '@/containers/User/hook'
import { useSelector } from '@/store'
import { useGuideQueue } from '@/store/userSlice/hooks'
import { jumpToOneclub } from '@/utils/user'
import Language from '../Language'
import styles from './index.module.less'

const UserLogin: React.FC = () => {
  const updata = useUpdate()
  const { t } = useTranslation()
  const languageStore = useSelector((state) => state.language)
  const countryMapDics = useSelector((state) => state.dictionary.countryMapDics)
  const { selfArea: selfCountry, accountType } = useSelector((state) => state.user.info) || {}
  const user = useSelector((state) => state.user.info)
  const languageList = useSelector((state) => state.user.languageList)
  const { logoutToOnclub } = useUserLogin()
  const [subMenu, setSubMenu] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [visible, setVisible] = useState(false)
  const handleClick = () => {
    localStorage.setItem('isNewUser', JSON.stringify(true))
    setVisible(true)
    setSubMenu(true)
    updata()
  }

  // 展示服务申请
  const showServiceApply = accountType === 'MAIN' && ['CN', 'MY', 'SG', 'TH', 'VN', 'HK', 'TW'].includes(selfCountry)

  // 只有香港越南才展示提示
  const { firstUnshowedKey, next: nextGuide, guideQueue, setShowed } = useGuideQueue(showServiceApply ? ['language', 'serviceApply'] : ['language'])

  const handleLangOpen = () => {
    setLangOpen(true)
    setSubMenu(false)
  }

  const closeAll = () => {
    setSubMenu(false)
    setLangOpen(false)
    setVisible(false)
  }

  const toOnclubAccountPage = useCallback(() => {
    jumpToOneclub('account')
  }, [])

  // 服务申请
  const applyService = useCallback(() => {
    jumpToOneclub(selfCountry === 'TW' ? 'createApply' : 'serviceApply')
    setShowed('serviceApply')
  }, [selfCountry, setShowed])

  const listConfig = useMemo(
    () => [
      {
        onClick: toOnclubAccountPage,
        icon: <User className={styles.icon} />,
        label: t('menu.account'),
        noArrow: true,
        show: true,
      },
      {
        onClick: applyService,
        icon: <ArrivalOrder className={styles.icon} />,
        label: t('serviceApply.title'),
        noArrow: true,
        tag: guideQueue.find((i) => i.key === 'serviceApply' && !i.showed) ? 'NEW' : null,
        show: showServiceApply,
      },
      {
        onClick: () => jumpToOneclub('manager'),
        icon: <Setting className={styles.icon} />,
        label: t('menu.administrator'),
        noArrow: true,
        show: accountType === 'MAIN',
      },
      {
        onClick: handleLangOpen,
        icon: <LanguageIcon className={styles.icon} />,
        label: languageList?.find((item) => item.lang === languageStore?.lang)?.label,
        show: true,
      },
      {
        icon: <Country className={styles.icon} />,
        label: countryMapDics?.[selfCountry]?.countryName || '',
        noArrow: true,
        noCursor: true,
        show: true,
      },
      {
        onClick: logoutToOnclub,
        icon: <LogOut className={styles.icon} />,
        label: t('frame.logout'),
        noArrow: true,
        show: true,
      },
    ],
    [countryMapDics, languageList, languageStore?.lang, logoutToOnclub, selfCountry, t, toOnclubAccountPage, applyService, guideQueue, accountType, showServiceApply]
  )
  const subMenuContainer = useMemo(
    () => (
      <div className={styles.settingList}>
        <div className={styles.accountInfo}>
          <div className={styles.name}>
            <span>{user.sellerName}</span>
            <Edit className={styles.edit} onClick={toOnclubAccountPage} />
          </div>
          <div className={styles.account}>
            <span>{user.oneshipName}</span>
            <span className={styles.email}>{user.email || user.phone}</span>
          </div>
        </div>
        <List>
          {listConfig.map((item, ind) => {
            const listItem = (
              <List.Item onClick={() => item.onClick && item.onClick()} className={styles.listItem} style={{ cursor: item.noCursor ? 'default' : 'pointer' }} key={ind}>
                <span className="flex align_c">
                  {item.icon}
                  <span style={{ paddingLeft: '8px', fontSize: '14px' }} className={styles.buttonText}>
                    {item.label}
                  </span>
                  {item.tag ? <span className={styles.menuTag}>{item.tag}</span> : null}
                </span>
                {item.noArrow ? null : <ChevronRight style={{ fontSize: 18 }} />}
              </List.Item>
            )
            return item.show ? listItem : null
          })}
        </List>
      </div>
    ),
    [listConfig, toOnclubAccountPage, user]
  )

  const overlay = useMemo(() => {
    if (subMenu) return subMenuContainer
    if (langOpen) return <Language setSubMenu={setSubMenu} setLangOpen={setLangOpen} closeAll={closeAll} />
  }, [langOpen, subMenu, subMenuContainer])

  const handleChange = (visible) => {
    if (!visible) {
      closeAll()
    }
  }

  const dropdownButton = (
    <Dropdown trigger={['click']} overlay={<Menu style={{ minWidth: '240px', padding: 0 }}>{overlay}</Menu>} visible={visible} onVisibleChange={handleChange}>
      <OneButton type="text" onClick={handleClick} className={styles.btn} id="user-dropdown">
        <Avatar className={styles.iconUser} />
        {user && <p className={styles.user}>{user.sellerName}</p>}
        {visible ? <ChevronUp style={{ fontSize: 18 }} /> : <ChevronDown style={{ fontSize: 18 }} />}
      </OneButton>
    </Dropdown>
  )
  return (
    <div className={styles.userLogin}>
      {firstUnshowedKey === 'serviceApply' ? (
        <OneTooltip
          title={
            <div className={styles.tooltip}>
              {t('serviceApply.guide')}
              <span onClick={nextGuide} className={styles.isee}>
                {' '}
                {t('common.Ikonw')}
              </span>
            </div>
          }
          placement="bottomRight"
          visible
          color="#fff"
        >
          {dropdownButton}
        </OneTooltip>
      ) : (
        dropdownButton
      )}

      {firstUnshowedKey === 'language' ? (
        <div className={styles.newUser}>
          <div className={styles.text}>👆👆{t('wallet.newCharge')}👆👆</div>
          <OneButton type="primary" onClick={nextGuide}>
            {t('wallet.newChargeBtn')}
          </OneButton>
        </div>
      ) : null}
    </div>
  )
}

UserLogin.displayName = 'UserLogin'
export default UserLogin
